import { FC, useState } from 'react';
import CheckboxDropdown from '../../../Components/Form/Inputs/CheckboxDropdown';
import { SelectInputOption } from '../../../Components/Form/Inputs/SelectInput';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './CalendarSpreadCalculator.module.scss';
import { ICalendarSpreadCalculationResult } from './CalendarSpreadDefinitions';

interface ICalendarSpreadCalculatorGridProps {
    calculationResult?: ICalendarSpreadCalculationResult;
}

enum SpreadPeriod {
    Spread = 'spread',
    ThreeMonthSpread = 'threeMonthSpread',
    SixMonthSpread = 'sixMonthSpread',
    NineMonthSpread = 'nineMonthSpread',
    TwelveMonthSpread = 'twelveMonthSpread',
}

const spreadPeriods = [
    SpreadPeriod.Spread,
    SpreadPeriod.ThreeMonthSpread,
    SpreadPeriod.SixMonthSpread,
    SpreadPeriod.NineMonthSpread,
    SpreadPeriod.TwelveMonthSpread,
];

const CalendarSpreadCalculatorGrid: FC<ICalendarSpreadCalculatorGridProps> = (props: ICalendarSpreadCalculatorGridProps) => {
    const [translations] = useLanguage();
    const [selectedPeriods, setSelectedPeriods] = useState<SpreadPeriod[]>([SpreadPeriod.Spread]);

    const spreadOptions: SelectInputOption<SpreadPeriod>[] = spreadPeriods.map((period) => {
        let label = '';
        switch (period) {
            case SpreadPeriod.Spread:
                label = translations.words.spread;
                break;
            case SpreadPeriod.ThreeMonthSpread:
                label = `3 ${translations.words.monthShort} ${translations.words.spread}`;
                break;
            case SpreadPeriod.SixMonthSpread:
                label = `6 ${translations.words.monthShort} ${translations.words.spread}`;
                break;
            case SpreadPeriod.NineMonthSpread:
                label = `9 ${translations.words.monthShort} ${translations.words.spread}`;
                break;
            default:
                label = `12 ${translations.words.monthShort} ${translations.words.spread}`;
        }
        return { label, value: period };
    });

    const deciles = [
        {
            label: 'Min',
            value: 0,
        },
        {
            label: '10%',
            value: 1,
        },
        {
            label: '20%',
            value: 2,
        },
        {
            label: '30%',
            value: 3,
        },
        {
            label: '40%',
            value: 4,
        },
        {
            label: '50%',
            value: 5,
        },
        {
            label: '60%',
            value: 6,
        },
        {
            label: '70%',
            value: 7,
        },
        {
            label: '80%',
            value: 8,
        },
        {
            label: '90%',
            value: 9,
        },
        {
            label: 'Max',
            value: 10,
        },
    ];

    const handleIntervalChange = (values: SpreadPeriod[]) => {
        const orderMap: Map<SpreadPeriod, number> = new Map();
        spreadOptions.forEach((option, index) => {
            orderMap.set(option.value, index);
        });
        const sortedValues = values.sort((a, b) => {
            const indexA = orderMap.get(a) ?? -1;
            const indexB = orderMap.get(b) ?? -1;
            return indexA - indexB;
        });
        setSelectedPeriods(sortedValues);
    };

    const getPeriodData = (period: SpreadPeriod) => {
        switch (period) {
            case SpreadPeriod.Spread:
                return props.calculationResult?.contract1;
            case SpreadPeriod.ThreeMonthSpread:
                return props.calculationResult?.threeMonths;
            case SpreadPeriod.SixMonthSpread:
                return props.calculationResult?.sixMonths;
            case SpreadPeriod.NineMonthSpread:
                return props.calculationResult?.nineMonths;
            case SpreadPeriod.TwelveMonthSpread:
                return props.calculationResult?.twelveMonths;
            default:
                return undefined;
        }
    };

    const getPeriodLabel = (period: SpreadPeriod) => {
        switch (period) {
            case SpreadPeriod.Spread:
                return translations.words.spread;
            case SpreadPeriod.ThreeMonthSpread:
                return `3 ${translations.words.monthShort}`;
            case SpreadPeriod.SixMonthSpread:
                return `6 ${translations.words.monthShort}`;
            case SpreadPeriod.NineMonthSpread:
                return `9 ${translations.words.monthShort}`;
            case SpreadPeriod.TwelveMonthSpread:
                return `12 ${translations.words.monthShort}`;
            default:
                return '';
        }
    };

    return (
        <div className={styles.calendar_spread_calculator_grid}>
            <div className={styles.calendar_spread_calculator_grid_interval}>
                <CheckboxDropdown
                    values={selectedPeriods}
                    options={spreadOptions}
                    handleOptionSelect={handleIntervalChange}
                    placeholder="Select Intervals"
                    track={{ eventCategory: 'CalendarSpreadCalculator', eventAction: 'SelectedIntervalsChanged', eventValue: selectedPeriods }}
                />
            </div>
            <div className={styles.calendar_spread_calculator_grid_table}>
                <div className={styles.calendar_spread_calculator_grid_table_row}>
                    <div className={styles.calendar_spread_calculator_grid_table_cell_empty}> </div>
                    <div className={styles.calendar_spread_calculator_grid_table_cell_average}>
                        <strong>{translations.text.averageShort}</strong>
                    </div>
                    {deciles.map((decile, index) => (
                        <div key={decile.value} className={styles[`calendar_spread_calculator_grid_table_cell_decile${index}`]}>
                            <strong>{decile.label}</strong>
                        </div>
                    ))}
                </div>
                {selectedPeriods.map((period) => {
                    const periodData = getPeriodData(period);
                    const periodLabel = getPeriodLabel(period);

                    return (
                        <div key={period} className={styles.calendar_spread_calculator_grid_table_row}>
                            <div className={styles.calendar_spread_calculator_grid_table_cell_header}>{periodLabel}</div>
                            <div className={styles.calendar_spread_calculator_grid_table_cell_average}>
                                {formattingService.formatPriceNumber(periodData?.average)}
                            </div>
                            {deciles.map((decile, index) => (
                                <div key={decile.value} className={styles.calendar_spread_calculator_grid_table_cell}>
                                    {formattingService.formatPriceNumber(periodData?.deciles?.[index], { zeroAsEmpty: true })}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CalendarSpreadCalculatorGrid;
